package com.karyadina.ui.screens.flashcards.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService
import com.karyadina.service.exportFlashcardsToPdf
import com.karyadina.service.sharePdf
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

enum class ImageFilter {
    ALL_CARDS,
    ONLY_WITH_IMAGES,
    ONLY_WITHOUT_IMAGES
}

enum class ExportScope {
    CURRENT_UNIT,
    SELECTED_UNITS,
    FULL_SUBJECT
}

data class ExportStats(
    val totalCards: Int,
    val cardsWithImages: Int,
    val totalUnits: Int
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ExportNotesToPdfScreen(
    subject: FlashCard_Subject,
    unit: StudyUnit,
    service: FlashcardService,
    onBack: () -> Unit,
    onExportComplete: (String?) -> Unit
) {
    var exportScope by remember { mutableStateOf<ExportScope>(ExportScope.CURRENT_UNIT) }
    var includeQuestions by remember { mutableStateOf(true) }
    var includeAnswers by remember { mutableStateOf(true) }
    var includeHints by remember { mutableStateOf(false) }
    var imageFilter by remember { mutableStateOf(ImageFilter.ALL_CARDS) }
    var isExporting by remember { mutableStateOf(false) }
    var exportProgress by remember { mutableStateOf(0f) }
    var exportMessage by remember { mutableStateOf("Preparing export...") }
    var exportedPdfPath by remember { mutableStateOf<String?>(null) }
    var showSuccessDialog by remember { mutableStateOf(false) }
    var selectedUnits by remember { mutableStateOf(setOf(unit.id)) }
    
    val scope = rememberCoroutineScope()
    val allUnits = remember { service.getUnits(subject.id) }
    
    // Calculate stats
    val stats = remember(exportScope, selectedUnits) {
        when (exportScope) {
            ExportScope.CURRENT_UNIT -> {
                val cards = service.getCards(unit.id)
                ExportStats(
                    totalCards = cards.size,
                    cardsWithImages = cards.count { it.imagePaths.isNotEmpty() || it.imagePath != null },
                    totalUnits = 1
                )
            }
            ExportScope.SELECTED_UNITS -> {
                val cards = selectedUnits.flatMap { unitId -> service.getCards(unitId) }
                ExportStats(
                    totalCards = cards.size,
                    cardsWithImages = cards.count { it.imagePaths.isNotEmpty() || it.imagePath != null },
                    totalUnits = selectedUnits.size
                )
            }
            ExportScope.FULL_SUBJECT -> {
                val cards = allUnits.flatMap { u -> service.getCards(u.id) }
                ExportStats(
                    totalCards = cards.size,
                    cardsWithImages = cards.count { it.imagePaths.isNotEmpty() || it.imagePath != null },
                    totalUnits = allUnits.size
                )
            }
        }
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Export to PDF") },
                navigationIcon = {
                    IconButton(onClick = onBack, enabled = !isExporting) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back")
                    }
                }
            )
        }
    ) { padding ->
        if (isExporting) {
            // Export Progress Screen
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .background(MaterialTheme.colorScheme.surface),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(24.dp),
                    modifier = Modifier.padding(32.dp)
                ) {
                    CircularProgressIndicator(
                        progress = {exportProgress},
                        modifier = Modifier.size(80.dp),
                        strokeWidth = 6.dp
                    )
                    Text(
                        exportMessage,
                        style = MaterialTheme.typography.titleMedium,
                        textAlign = TextAlign.Center
                    )
                    Text(
                        "${(exportProgress * 100).toInt()}%",
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        } else {
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Export Scope Selection
                item {
                    Text(
                        "Export Scope",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
                
                item {
                    ExportScopeCard(
                        scope = ExportScope.CURRENT_UNIT,
                        isSelected = exportScope == ExportScope.CURRENT_UNIT,
                        title = "Current Unit Only",
                        description = "${unit.name} • ${stats.totalCards} cards",
                        icon = Icons.Filled.FolderOpen,
                        onClick = { 
                            exportScope = ExportScope.CURRENT_UNIT
                            selectedUnits = setOf(unit.id)
                        }
                    )
                }
                
                item {
                    ExportScopeCard(
                        scope = ExportScope.SELECTED_UNITS,
                        isSelected = exportScope == ExportScope.SELECTED_UNITS,
                        title = "Selected Units",
                        description = "Choose specific units to export",
                        icon = Icons.Filled.CheckCircle,
                        onClick = { exportScope = ExportScope.SELECTED_UNITS }
                    )
                }
                
                if (exportScope == ExportScope.SELECTED_UNITS && allUnits.size > 1) {
                    items(allUnits) { u ->
                        UnitSelectionItem(
                            unit = u,
                            isSelected = u.id in selectedUnits,
                            onToggle = {
                                selectedUnits = if (u.id in selectedUnits) {
                                    selectedUnits - u.id
                                } else {
                                    selectedUnits + u.id
                                }
                            }
                        )
                    }
                }
                
                item {
                    ExportScopeCard(
                        scope = ExportScope.FULL_SUBJECT,
                        isSelected = exportScope == ExportScope.FULL_SUBJECT,
                        title = "Full Subject",
                        description = "${subject.name} • All ${allUnits.size} units",
                        icon = Icons.Filled.Book,
                        onClick = { exportScope = ExportScope.FULL_SUBJECT }
                    )
                }
                
                // Export Options
                item {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Content Options",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                }
                
                item {
                    ElevatedCard(modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text("Include Questions", style = MaterialTheme.typography.bodyLarge)
                                    Text(
                                        "Show question text on each page",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    )
                                }
                                Switch(checked = includeQuestions, onCheckedChange = { includeQuestions = it })
                            }
                            
                            HorizontalDivider()
                            
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text("Include Answers", style = MaterialTheme.typography.bodyLarge)
                                    Text(
                                        "Show answer text on each page",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    )
                                }
                                Switch(checked = includeAnswers, onCheckedChange = { includeAnswers = it })
                            }
                            
                            HorizontalDivider()
                            
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text("Include Hints", style = MaterialTheme.typography.bodyLarge)
                                    Text(
                                        "Show hint text if available",
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    )
                                }
                                Switch(checked = includeHints, onCheckedChange = { includeHints = it })
                            }
                            
                            HorizontalDivider()
                            
                            Column(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    "Image Filter",
                                    style = MaterialTheme.typography.bodyLarge,
                                    modifier = Modifier.padding(bottom = 8.dp)
                                )
                                
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { imageFilter = ImageFilter.ALL_CARDS },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    RadioButton(
                                        selected = imageFilter == ImageFilter.ALL_CARDS,
                                        onClick = { imageFilter = ImageFilter.ALL_CARDS }
                                    )
                                    Column(modifier = Modifier.padding(start = 8.dp)) {
                                        Text("All Cards", style = MaterialTheme.typography.bodyMedium)
                                        Text(
                                            "${stats.totalCards} cards",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                        )
                                    }
                                }
                                
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { imageFilter = ImageFilter.ONLY_WITH_IMAGES },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    RadioButton(
                                        selected = imageFilter == ImageFilter.ONLY_WITH_IMAGES,
                                        onClick = { imageFilter = ImageFilter.ONLY_WITH_IMAGES }
                                    )
                                    Column(modifier = Modifier.padding(start = 8.dp)) {
                                        Text("Only With Images", style = MaterialTheme.typography.bodyMedium)
                                        Text(
                                            "${stats.cardsWithImages} cards",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                        )
                                    }
                                }
                                
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { imageFilter = ImageFilter.ONLY_WITHOUT_IMAGES },
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    RadioButton(
                                        selected = imageFilter == ImageFilter.ONLY_WITHOUT_IMAGES,
                                        onClick = { imageFilter = ImageFilter.ONLY_WITHOUT_IMAGES }
                                    )
                                    Column(modifier = Modifier.padding(start = 8.dp)) {
                                        Text("Only Without Images", style = MaterialTheme.typography.bodyMedium)
                                        Text(
                                            "${stats.totalCards - stats.cardsWithImages} cards",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
                
                // Stats Preview
                item {
                    ElevatedCard(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.elevatedCardColors(
                            containerColor = MaterialTheme.colorScheme.primaryContainer
                        )
                    ) {
                        Column(modifier = Modifier.padding(20.dp)) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Icon(Icons.Filled.PictureAsPdf, null, tint = MaterialTheme.colorScheme.primary)
                                Text(
                                    "Export Summary",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                            Spacer(Modifier.height(12.dp))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceEvenly
                            ) {
                                StatItem("Units", stats.totalUnits.toString(), Icons.Filled.FolderOpen)
                                StatItem("Cards", stats.totalCards.toString(), Icons.Filled.Style)
                                StatItem("Images", stats.cardsWithImages.toString(), Icons.Filled.Image)
                            }
                        }
                    }
                }
                
                // Export Button
                item {
                    Button(
                        onClick = {
                            if (stats.totalCards == 0 || (imageFilter == ImageFilter.ONLY_WITH_IMAGES && stats.cardsWithImages == 0) || (imageFilter == ImageFilter.ONLY_WITHOUT_IMAGES && stats.totalCards == stats.cardsWithImages)) {
                                return@Button
                            }
                            
                            scope.launch {
                                isExporting = true
                                exportProgress = 0f
                                exportMessage = "Collecting flashcards..."
                                
                                try {
                                    // Collect cards based on scope
                                    val cardsToExport = when (exportScope) {
                                        ExportScope.CURRENT_UNIT -> service.getCards(unit.id)
                                        ExportScope.SELECTED_UNITS -> selectedUnits.flatMap { unitId -> 
                                            service.getCards(unitId) 
                                        }
                                        ExportScope.FULL_SUBJECT -> allUnits.flatMap { u -> 
                                            service.getCards(u.id) 
                                        }
                                    }.let { cards ->
                                        when (imageFilter) {
                                            ImageFilter.ALL_CARDS -> cards
                                            ImageFilter.ONLY_WITH_IMAGES -> cards.filter { 
                                                it.imagePaths.isNotEmpty() || it.imagePath != null 
                                            }
                                            ImageFilter.ONLY_WITHOUT_IMAGES -> cards.filter { 
                                                it.imagePaths.isEmpty() && it.imagePath == null 
                                            }
                                        }
                                    }
                                    
                                    if (cardsToExport.isEmpty()) {
                                        isExporting = false
                                        exportMessage = "No cards to export"
                                        return@launch
                                    }
                                    
                                    exportProgress = 0.2f
                                    exportMessage = "Preparing ${cardsToExport.size} cards..."
                                    
                                    // Generate PDF title
                                    val pdfTitle = when (exportScope) {
                                        ExportScope.CURRENT_UNIT -> "${subject.name}_${unit.name}"
                                        ExportScope.SELECTED_UNITS -> "${subject.name}_selected_units"
                                        ExportScope.FULL_SUBJECT -> subject.name
                                    }
                                    
                                    exportProgress = 0.4f
                                    exportMessage = "Generating PDF..."
                                    
                                    // Export flashcards to PDF
                                    val pdfPath = exportFlashcardsToPdf(
                                        cards = cardsToExport,
                                        units = allUnits,
                                        title = pdfTitle,
                                        includeQuestions = includeQuestions,
                                        includeAnswers = includeAnswers,
                                        includeHints = includeHints,
                                        onlyImages = imageFilter == ImageFilter.ONLY_WITH_IMAGES
                                    )
                                    
                                    exportProgress = 1f
                                    exportMessage = "Export complete!"
                                    exportedPdfPath = pdfPath
                                    
                                    delay(500)
                                    isExporting = false
                                    showSuccessDialog = pdfPath != null
                                    
                                    if (pdfPath == null) {
                                        exportMessage = "Export failed"
                                    }
                                } catch (e: Exception) {
                                    isExporting = false
                                    exportMessage = "Export failed: ${e.message}"
                                }
                            }
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        enabled = stats.totalCards > 0 && !(imageFilter == ImageFilter.ONLY_WITH_IMAGES && stats.cardsWithImages == 0) && !(imageFilter == ImageFilter.ONLY_WITHOUT_IMAGES && stats.totalCards == stats.cardsWithImages)
                    ) {
                        Icon(Icons.Filled.FileDownload, null)
                        Spacer(Modifier.width(8.dp))
                        Text(
                            "Export to PDF",
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
                
                if (stats.cardsWithImages == 0 && imageFilter == ImageFilter.ALL_CARDS) {
                    item {
                        Surface(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(8.dp),
                            color = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.3f)
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Icon(
                                    Icons.Filled.Info,
                                    null,
                                    tint = MaterialTheme.colorScheme.secondary,
                                    modifier = Modifier.size(20.dp)
                                )
                                Text(
                                    "No scanned images found. PDF will include text-only cards. Use the camera to scan notes for richer content.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSecondaryContainer
                                )
                            }
                        }
                    }
                }
                
                if (imageFilter == ImageFilter.ONLY_WITH_IMAGES && stats.cardsWithImages == 0) {
                    item {
                        Surface(
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(8.dp),
                            color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.3f)
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Icon(
                                    Icons.Filled.Info,
                                    null,
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(20.dp)
                                )
                                Text(
                                    "No scanned images found. Use the camera to scan notes from your flashcards.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onErrorContainer
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    
    // Success Dialog
    if (showSuccessDialog && exportedPdfPath != null) {
        AlertDialog(
            onDismissRequest = {
                showSuccessDialog = false
                onExportComplete(exportedPdfPath)
            },
            icon = {
                Icon(
                    Icons.Filled.CheckCircle,
                    null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(48.dp)
                )
            },
            title = { Text("Export Successful!") },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Your flashcards have been exported to PDF.")
                    Text(
                        "Location: $exportedPdfPath",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                    )
                }
            },
            confirmButton = {
                Button(onClick = {
                    showSuccessDialog = false
                    onExportComplete(exportedPdfPath)
                }) {
                    Text("Done")
                }
            },
            dismissButton = {
                TextButton(onClick = {
                    val pdfPath = exportedPdfPath
                    if (pdfPath != null) {
                        sharePdf(pdfPath)
                    }
                }) {
                    Text("Share")
                }
            }
        )
    }
}

@Composable
fun ExportScopeCard(
    scope: ExportScope,
    isSelected: Boolean,
    title: String,
    description: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    onClick: () -> Unit
) {
    ElevatedCard(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = if (isSelected) {
                MaterialTheme.colorScheme.primaryContainer
            } else {
                MaterialTheme.colorScheme.surface
            }
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                icon,
                null,
                modifier = Modifier.size(40.dp),
                tint = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
            )
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    description,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
                )
            }
            if (isSelected) {
                Icon(
                    Icons.Filled.CheckCircle,
                    null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
    }
}

@Composable
fun UnitSelectionItem(
    unit: StudyUnit,
    isSelected: Boolean,
    onToggle: () -> Unit
) {
    ElevatedCard(
        onClick = onToggle,
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 32.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    unit.name,
                    style = MaterialTheme.typography.bodyLarge
                )
                Text(
                    "${unit.totalCards} cards",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                )
            }
            Checkbox(
                checked = isSelected,
                onCheckedChange = { onToggle() }
            )
        }
    }
}
